import { useMemo } from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import { TestExecutionField } from '@elementary/common-ui/services/gql/types';
import { SortDirection } from '@elementary/common-ui/components/Table/types';
import {
  mapUrlFiltersToGraphqlFilters,
  parseGraphqlFilterOptions,
} from '@elementary/common-ui/utils/graphqlFilters';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import { TEST_RESULTS_FILTER_OPTIONS } from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';
import {
  TEST_RESULTS_TABLE_FILTERS,
  TEST_RESULTS_TABLE_SORT,
} from '@elementary/common-ui/constants/urlParams';
import {
  ApiTestResultsFilterOptionsResponse,
  getTestResultsFilterOptionsQuery,
} from './api';
import { COLUMN_TO_FIELD_MAP, FIELD_TO_COLUMN_MAP } from './consts';

const DEFAULT_SORT = {
  id: 'latestRunStatus',
  direction: 'asc',
};

type Args = {
  enabled?: boolean;
};

const useTestResultsQueryContext = ({ enabled = true }: Args = {}) => {
  const envid = useSelectedEnv();
  const { selectedTreeNode, selectedTreeFolderNodes } = useSelectedTreeItem();
  const { urlFiltersDebounced } = useUrlFilters({
    urlKey: TEST_RESULTS_TABLE_FILTERS,
  });
  const [searchParams] = useSearchParams();

  const hasSelectedNodes = useMemo(
    () => !!(selectedTreeFolderNodes?.length || selectedTreeNode?.id),
    [selectedTreeFolderNodes, selectedTreeNode],
  );

  const { selectedAssetIds, selectedTestIds } = useMemo(() => {
    const selectedAssetIds: string[] = [];
    const selectedTestIds: string[] = [];
    if (selectedTreeFolderNodes) {
      selectedTreeFolderNodes.forEach((node) => {
        if (node.resourceType !== 'test') {
          selectedAssetIds.push(node.id);
        } else {
          selectedTestIds.push(node.id);
        }
      });
    } else if (selectedTreeNode) {
      if (selectedTreeNode.resourceType !== 'test') {
        selectedAssetIds.push(selectedTreeNode.id);
      } else {
        selectedTestIds.push(selectedTreeNode.id);
      }
    }
    return { selectedAssetIds, selectedTestIds };
  }, [selectedTreeFolderNodes, selectedTreeNode]);

  const selectedIdsHash = useMemo(() => {
    const testsHash = hashArray(selectedTestIds);
    const assetsHash = hashArray(selectedAssetIds);
    return `${testsHash}-${assetsHash}`;
  }, [selectedTestIds, selectedAssetIds]);

  const urlSort: { id: string; direction: SortDirection } | null = useMemo(
    () =>
      JSON.parse(searchParams.get(TEST_RESULTS_TABLE_SORT) || 'null') ||
      DEFAULT_SORT,
    [searchParams],
  );

  const testExecutionFilters = useMemo(
    () =>
      mapUrlFiltersToGraphqlFilters(urlFiltersDebounced, COLUMN_TO_FIELD_MAP),
    [urlFiltersDebounced],
  );

  const sort = useMemo(() => {
    const localUrlSort = urlSort || DEFAULT_SORT;
    const field = COLUMN_TO_FIELD_MAP[localUrlSort.id];
    if (!field) return undefined;
    return {
      field,
      direction: localUrlSort.direction.toUpperCase() as 'ASC' | 'DESC',
    };
  }, [urlSort]);

  const { data: filterOptionsData, isFetching: isFilterOptionsFetching } =
    useGraphqlQuery<ApiTestResultsFilterOptionsResponse>({
      queryKey: [
        TEST_RESULTS_FILTER_OPTIONS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
      ],
      query: getTestResultsFilterOptionsQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
      },
      enabled: enabled && hasSelectedNodes,
      envId: envid,
    });

  const filterOptions = useMemo(
    () =>
      parseGraphqlFilterOptions<TestExecutionField>(
        filterOptionsData?.latestTestExecutionsFilterOptions || [],
        FIELD_TO_COLUMN_MAP,
      ),
    [filterOptionsData],
  );

  const { data: unscopedFilterOptionsData } =
    useGraphqlQuery<ApiTestResultsFilterOptionsResponse>({
      queryKey: [
        TEST_RESULTS_FILTER_OPTIONS,
        envid,
        'unscoped',
        DEFAULT_DAYS_BACK,
      ],
      query: getTestResultsFilterOptionsQuery,
      variables: {
        testIds: null,
        assetIds: null,
        daysBack: DEFAULT_DAYS_BACK,
      },
      enabled: enabled && hasSelectedNodes,
      envId: envid,
    });

  const unscopedFilterOptions = useMemo(
    () =>
      parseGraphqlFilterOptions<TestExecutionField>(
        unscopedFilterOptionsData?.latestTestExecutionsFilterOptions || [],
        FIELD_TO_COLUMN_MAP,
      ),
    [unscopedFilterOptionsData],
  );

  return {
    envid,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    testExecutionFilters,
    urlSort,
    sort,
    filterOptions,
    unscopedFilterOptions,
    isFilterOptionsFetching,
  };
};

export default useTestResultsQueryContext;
